import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Store } from '@ngrx/store';
import { take, switchMap, tap, of } from 'rxjs';
import { IPeerGroupCompetitors } from '../../../../../../core/models/results.model';
import { changeActiveSideTab, loadSidenavData, setUniverseSelection, submitSearch } from '../../../../../../state/action';
import { AppState, IUniverseSelection } from '../../../../../../state/app.state';
import { selectSidenavDataByTabIdAndKey } from '../../../../../../state/selectors';
import { GeneralInnovationActivityResultsService } from '../../../../services/general-innovation-activity-results.service';
import { CommonModule } from '@angular/common';
import { NgxPaginationModule } from 'ngx-pagination';
import { RouterLink, RouterOutlet } from '@angular/router';
import { NullableNumberPipe } from '../../../../../../shared/pipes/nullable-number.pipe';
import { MatDialog } from '@angular/material/dialog';
import { UniverseSelectorComponent } from 'src/app/shared/components/universe-selector/universe-selector.component';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { REGION_CODES } from 'public/country-region-codes';
import * as XLSX from 'xlsx';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MainNavbarComponent } from 'src/app/core/components/main-navbar/main-navbar.component';
@Component({
  selector: 'app-private-competitors',
  standalone: true,
  imports: [
    CommonModule,
    NgxPaginationModule,
    ReactiveFormsModule,
    FormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatSlideToggleModule,
    MainNavbarComponent,
    MatTooltipModule,
    MatOptionModule,
    MatCheckboxModule,
    MatAutocompleteModule,
    MatInputModule,
    MatIconModule,
    NullableNumberPipe
  ], templateUrl: './private-competitors.component.html',
  styleUrl: './private-competitors.component.css'
})
export class PrivateCompetitorsComponent implements OnInit, OnChanges {
  @Input() tabId!: string;
  @Input() query: string | null = null;
  @Input() gsubind: string[] | null = null;
  @Input() entityids: string[] | null = null;
  @Input() tab_name: string | null = null;
  @Input() side_nav_name: string | null = null;
  entityid: string | undefined;
  showDataMissing: boolean=false;

  constructor(
    private store: Store<AppState>,
    private apiService: GeneralInnovationActivityResultsService,
    private dialog: MatDialog
  ) { }

  showResults: boolean = false;
  competitors_list_loading_failed: boolean = false;
  competitors_list: IPeerGroupCompetitors[] = [];
  showQueryBox: boolean = false;
  company_data: IPeerGroupCompetitors | undefined = undefined;
  filteredCompanyList: IPeerGroupCompetitors[] = [];
  paginator_1_page: number = 1;
  showOnlyFavorites: boolean = false;
  searchQuery: string = '';
  countriesControl = new FormControl<string[]>([]);
  expandedRegions: Set<string> = new Set();

  developedMarketRegion = REGION_CODES.find(region => region.region === 'Developed Markets');
  developedMarketCountries = this.developedMarketRegion
    ? this.developedMarketRegion.countries.map(c => c.code)
    : [];

  countryFilterOptions = REGION_CODES;

  avg_portfolio_size_gics: number = 0;
  avg_recent_growth: number = 0;
  avg_innovation_quality: number = 0;

  // Sorting state
  sortColumn: string = 'innovation_score';
  sortDirection: 'asc' | 'desc' = 'desc';

  // Category system
  selectedCategory: string = 'all_competitors';
  categoryTabs = [
    {
      key: 'all_competitors',
      label: 'All Competitors',
      tooltip: 'All companies in GICS, no filter, innovation score ranking.',
      description: 'All companies in the selected peer group, ranked by innovation score.'
    },
    {
      key: 'innovation_leaders',
      label: 'Innovation Leaders',
      tooltip: 'Innovation Leaders are companies that rank highest among competitors based on their innovation score.',
      description: 'Innovation Leaders are the 50 companies that stand out with a strong innovation activity.'
    },
    {
      key: 'growth_leaders',
      label: 'Growth Leaders',
      tooltip: 'Growth Leaders are companies with the highest relative growth.',
      description: 'Growth Leaders are the 50 companies which show the most significant recent acceleration.'
    },
    {
      key: 'size_leaders',
      label: 'Size Leaders',
      tooltip: 'Size Leaders are companies with the highest number of inventions published in the last 10 years.',
      description: 'Size Leaders are the 50 companies with the largest innovation portfolios.'
    },
    {
      key: 'quality_leaders',
      label: 'Quality Leaders',
      tooltip: 'Quality Leaders are companies with the highest innovation quality.',
      description: 'Quality Leaders are the 50 companies that prioritize quality over quantity.'
    },
  ];

  ngOnInit(): void { }

  // Toggle query box
  toggleQueryBox(): void {
    this.showQueryBox = !this.showQueryBox;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tabId']) {
      this.resetState();
      if (this.gsubind && this.gsubind[0]) {
        this.getCompetitors();
      }
    }
  }

  resetState(): void {
    this.showResults = false;
    this.showDataMissing = false;
    this.competitors_list = [];
    this.competitors_list_loading_failed = false;
    this.selectedCategory = 'all_competitors';
  }

  getCompetitors(): void {
    // this.countriesControl.setValue(this.developedMarketCountries);
    this.entityid = this.entityids![0];
    this.showOnlyFavorites = false;
    this.showResults = true;
    this.store
      .select(selectSidenavDataByTabIdAndKey(this.tabId, 'company_private_competitors'))
      .pipe(
        take(1),
        switchMap((data) => {
          if (!data) {
            return this.apiService.getCompetitiveBenchmarkingPeerGroup({ universeSelection: { include_private: true, include_public: false } }, this.tabId, this.entityids![0], this.gsubind![0]).pipe(
              tap((res) => {
                if (res.status === 204 || !res.body?.payload?.results?.length) {
                  this.showDataMissing = true;
                  return;
                }
                const payload = res.body!.payload;

                if (payload.tabId !== this.tabId) {
                                  this.store.dispatch(loadSidenavData({
                  tabId: payload.tabId,
                  sidenavKey: 'company_private_competitors',
                  data: payload.results
                }));
                  return;
                }

                if (payload.results != null) {
                  this.store.dispatch(loadSidenavData({
                    tabId: this.tabId,
                    sidenavKey: 'company_private_competitors',
                    data: payload.results
                  }));
                  this.processCompetitorData(payload.results);
                } else {
                  this.competitors_list_loading_failed = true;
                }
              })
            );
          } else {
            this.competitors_list_loading_failed = false;
            this.processCompetitorData(data);
            return of();
          }
        })
      )
      .subscribe({
        // next: () =>console.log ('Competitor data processed.'),
        error: () => (this.competitors_list_loading_failed = true),
      });
  }

  processCompetitorData(data: IPeerGroupCompetitors[]): void {
    this.competitors_list = data.map((company) => ({
      ...company,
      favorite: company.favorite ?? false,
    }));


    this.filteredCompanyList = [...this.competitors_list];
    this.applyFilters();
  }

  // Selection state for table
  selectedCompanies: Set<string> = new Set();

  toggleFavorite(company: IPeerGroupCompetitors): void {
    const updatedCompany = { ...company, favorite: !company.favorite };
    const index = this.competitors_list.indexOf(company);
    if (index !== -1) {
      this.competitors_list[index] = updatedCompany;
    }
    this.applyFilters();
  }

  // Toggle company selection (using star icon)
  toggleCompanySelection(company: IPeerGroupCompetitors): void {
    if (this.selectedCompanies.has(company.entityid)) {
      this.selectedCompanies.delete(company.entityid);
    } else {
      this.selectedCompanies.add(company.entityid);
    }
  }

  // Check if company is selected
  isCompanySelected(entityid: string): boolean {
    return this.selectedCompanies.has(entityid);
  }

  toggleExpand(region: string): void {
    if (this.expandedRegions.has(region)) {
      this.expandedRegions.delete(region);
    } else {
      this.expandedRegions.add(region);
    }
  }

  isExpanded(region: string): boolean {
    return this.expandedRegions.has(region);
  }

  toggleRegionSelection(region: any): void {
    const allSelected = this.isRegionFullySelected(region);
    const regionCountryCodes = region.countries.map((c: any) => c.code);

    if (allSelected) {
      const updatedSelection = (this.countriesControl.value ?? []).filter(
        (value: string) => !regionCountryCodes.includes(value)
      );
      this.countriesControl.setValue(updatedSelection);
    } else {
      const updatedSelection = Array.from(
        new Set([...(this.countriesControl.value ?? []), ...regionCountryCodes])
      );
      this.countriesControl.setValue(updatedSelection);
    }
    this.applyFilters();
  }

  toggleCountrySelection(countryCode: string, region: any): void {
    const currentSelection = this.countriesControl.value ?? [];
    const isSelected = currentSelection.includes(countryCode);

    if (isSelected) {
      this.countriesControl.setValue(
        currentSelection.filter((value: string) => value !== countryCode)
      );
    } else {
      this.countriesControl.setValue([...currentSelection, countryCode]);
    }
    this.applyFilters();
  }

  isRegionFullySelected(region: any): boolean {
    const regionCountryCodes = region.countries.map((c: any) => c.code);
    const selectedCodes = this.countriesControl.value ?? [];
    return regionCountryCodes.every((code: string) => selectedCodes.includes(code));
  }
  filterFavorites(): void {
    this.showOnlyFavorites = !this.showOnlyFavorites;
    this.applyFilters();
  }


  //log(size+1) log/log_max
  normalizeInnovationScore(companies: IPeerGroupCompetitors[]): void {

    companies.forEach(company => {
      company.normalized_size = Math.log((company.invention_count_120m == null ? 0 : company.invention_count_120m) + 1);
    });

    const maxLogSize = Math.max(...companies.map(c => c.normalized_size));

    companies.forEach(company => {
      company.normalized_size = company.normalized_size / maxLogSize;
    });


    companies.forEach(company => {
      company.normalized_relative = Math.log((company.relative_growth_12m == null ? -100 : company.relative_growth_12m) + 100 + 1);//100 for percentage offset(0 to infinity) and 1 for log
    });

    const maxLogRelativeGrowth = Math.max(...companies.map(c => c.normalized_relative));

    companies.forEach(company => {
      company.normalized_relative = company.normalized_relative / maxLogRelativeGrowth;
    });


    companies.forEach(company => {
      const quality = company.quality_average_12m;
      const adjustedQuality = (quality === null) ? 0.1 : quality; // Default to 0.1 if null/NA/empty
      company.innovation_score_raw = company.normalized_size * company.normalized_relative * adjustedQuality;
    });



    const minScore = 0;
    const maxScore = Math.max(...companies.map(c => c.innovation_score_raw));

    companies.forEach(company => {
      company.innovation_score =
        minScore === maxScore
          ? 100
          : ((company.innovation_score_raw - minScore) / (maxScore - minScore)) * 100;
    });
  }


  rankInnovationScore(companies: IPeerGroupCompetitors[]) {
    companies.sort((a, b) => b.innovation_score - a.innovation_score);
    companies.forEach((company, index) => {
      company.rank = index + 1;
    });
  }

  applyFilters(): void {
    this.filteredCompanyList = this.competitors_list;

    // Apply category filter first
    this.filteredCompanyList = this.applyCategoryFilter(this.filteredCompanyList);

    // Apply category ranking BEFORE filtering by selection
    // This ensures ranks are set and won't be recalculated when showing selection only
    this.applyCategoryRanking(this.filteredCompanyList);

    if (this.countriesControl.value?.length) {
      this.filteredCompanyList = this.filteredCompanyList.filter((company) =>
        this.countriesControl.value!.includes(company.country)
      );
    }

    if (this.showOnlyFavorites) {
      // Show selected companies (starred/selected via star icon)
      // Ranks are already set above, so they won't be recalculated
      this.filteredCompanyList = this.filteredCompanyList.filter((company) => 
        this.selectedCompanies.has(company.entityid) || company.favorite
      );
      this.paginator_1_page = 1;
      // Skip sorting when showing selection only to preserve ranks
      return;
    }

    if (this.searchQuery) {
      this.filteredCompanyList = this.filteredCompanyList.filter((company) =>
        company.entity_name.toLowerCase().includes(this.searchQuery.toLowerCase())
      );
    }
    
  }

  // Apply category filter
  applyCategoryFilter(companies: IPeerGroupCompetitors[]): IPeerGroupCompetitors[] {
    if (!companies || companies.length === 0) return [];

    switch (this.selectedCategory) {
      case 'innovation_leaders':
        // Top 50 with innovation score > 0
        return companies
          .filter(company => (company.innovation_score ?? 0) > 0)
          .sort((a, b) => (b.innovation_score ?? 0) - (a.innovation_score ?? 0))
          .slice(0, 50);

      case 'growth_leaders':
        // Top 50 by growth with minimum 10 inventions in last year and relative growth > 0
        return companies
          .filter(company => ((company.invention_count_12m ?? 0) >= 10) && (company.relative_growth_12m ?? 0) > 0)
          .sort((a, b) => (b.relative_growth_12m ?? 0) - (a.relative_growth_12m ?? 0))
          .slice(0, 50);

      case 'size_leaders':
        // Top 50 by size (10y invention count)
        return companies
          .filter(company => (company.invention_count_120m ?? 0) > 0)
          .sort((a, b) => (b.invention_count_120m ?? 0) - (a.invention_count_120m ?? 0))
          .slice(0, 50);

      case 'quality_leaders':
        // Top 50 by quality with minimum 10 inventions in last year and quality average > 69
        return companies
          .filter(company => ((company.invention_count_12m ?? 0) >= 10) && (company.quality_average_12m ?? 0) > 69)
          .sort((a, b) => (b.quality_average_12m ?? 0) - (a.quality_average_12m ?? 0))
          .slice(0, 50);

      case 'upcoming':
        // Top 50 by innovation score increase (non-negative)
        return companies
          .filter(company => (company.innovation_score_change ?? 0) >= 0)
          .sort((a, b) => (b.innovation_score_change ?? 0) - (a.innovation_score_change ?? 0))
          .slice(0, 50);

      case 'all_competitors':
      default:
        return companies.sort((a, b) => (b.innovation_score ?? 0) - (a.innovation_score ?? 0));
    }
  }

  // Apply category-specific ranking
  private applyCategoryRanking(companies: IPeerGroupCompetitors[]): void {
    switch (this.selectedCategory) {
      case 'innovation_leaders':
        companies.sort((a, b) => (b.innovation_score ?? 0) - (a.innovation_score ?? 0));
        companies.forEach((c, i) => c.rank = i + 1);
        break;

      case 'growth_leaders':
        companies.forEach((c, i) => c.rank = i + 1);
        break;

      case 'size_leaders':
        companies.forEach((c, i) => c.rank = i + 1);
        break;

      case 'quality_leaders':
        // Already sorted by quality in applyCategoryFilter, just assign ranks
        companies.forEach((c, i) => c.rank = i + 1);
        break;

      case 'upcoming':
        companies.forEach((c, i) => c.rank = i + 1);
        break;

      case 'all_competitors':
      default:
        companies.sort((a, b) => (b.innovation_score ?? 0) - (a.innovation_score ?? 0));
        companies.forEach((c, i) => c.rank = i + 1);
        break;
    }
  }
  sort(column: string): void {
    if (this.selectedCategory !== 'all_competitors') {
      return;
    }
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'desc';
    }
    this.applySorting();
  }

  applySorting(): void {
    if (!this.sortColumn) return;
    this.paginator_1_page = 1;
    this.filteredCompanyList.sort((a, b) => {
      let aVal: any = 0, bVal: any = 0;

      switch (this.sortColumn) {
        case 'rank':
          aVal = a.rank || 0;
          bVal = b.rank || 0;
          break;
        case 'size':
          aVal = a.invention_count_120m || 0;
          bVal = b.invention_count_120m || 0;
          break;
        case 'growth':
          aVal = a.relative_growth_12m || 0;
          bVal = b.relative_growth_12m || 0;
          break;
        case 'quality':
          aVal = a.quality_average_12m || 0;
          bVal = b.quality_average_12m || 0;
          break;
        case 'innovation_score':
          aVal = a.innovation_score || 0;
          bVal = b.innovation_score || 0;
          break;
        case 'score_change':
          aVal = (a as any).innovation_score_change || 0;
          bVal = (b as any).innovation_score_change || 0;
          break;
        case 'rank_change':
          aVal = (a as any).pct_rank_climbed || 0;
          bVal = (b as any).pct_rank_climbed || 0;
          break;
      }

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return this.sortDirection === 'asc' 
          ? aVal.localeCompare(bVal) 
          : bVal.localeCompare(aVal);
      }
      return this.sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }

  getSortIcon(column: string): string {
    if (this.selectedCategory !== 'all_competitors') return 'bi-arrow-down-up';
    if (this.sortColumn !== column) return 'bi-arrow-down-up';
    return this.sortDirection === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down';
  }
  selectCategory(categoryKey: string): void {
    this.selectedCategory = categoryKey;
    this.paginator_1_page = 1;
    if (categoryKey !== 'all_competitors') {
      this.sortColumn = 'innovation_score';
      this.sortDirection = 'desc';
    }
    this.applyFilters();
  }

  getCurrentCategoryLabel(): string {
    const category = this.categoryTabs.find(cat => cat.key === this.selectedCategory);
    return category ? category.label : 'All Competitors';
  }

  getCurrentCategoryDescription(): string {
    const category = this.categoryTabs.find(cat => cat.key === this.selectedCategory);
    return category ? category.description : '';
  }

  getCurrentCategoryTooltip(): string {
    const category = this.categoryTabs.find(cat => cat.key === this.selectedCategory);
    return category ? category.tooltip : '';
  }

  // Helper methods to safely access monitoring fields for table rows
  getCompanyScoreChange(company: IPeerGroupCompetitors): number | null {
    const comp = company as any;
    return comp.innovation_score_change !== null && comp.innovation_score_change !== undefined ? comp.innovation_score_change : null;
  }

  getCompanyRankChange(company: IPeerGroupCompetitors): number | null {
    const comp = company as any;
    return comp.pct_rank_climbed !== null && comp.pct_rank_climbed !== undefined ? comp.pct_rank_climbed : null;
  }

  hasCompanyScoreChange(company: IPeerGroupCompetitors): boolean {
    const comp = company as any;
    return comp.innovation_score_change !== null && comp.innovation_score_change !== undefined;
  }

  hasCompanyRankChange(company: IPeerGroupCompetitors): boolean {
    const comp = company as any;
    return comp.pct_rank_climbed !== null && comp.pct_rank_climbed !== undefined;
  }
  // Check if current category has results
  hasCategoryResults(): boolean {
    return this.filteredCompanyList.length > 0;
  }

  newSearch(company: IPeerGroupCompetitors) {
    if (!company.gsubind_extended ) {
      company.gsubind_extended = company.gsubind![0];
    }
    const searchParams = { 'companies': [company] };

    this.store.dispatch(submitSearch({ searchParams, searchType: 'company' }));
  }
  getBracketColor(bracket: number | undefined): string {
    if (!bracket) return 'gray';
    const colors = ['dark-red', 'dark-red', 'gray', 'dark-green', 'dark-green'];
    return colors[bracket - 1] || 'gray';
  }
  getBracketTooltip(bracket: number | undefined) {
    if (!bracket) return '';
    if (bracket === 1) return 'Among the bottom 5% globally.';
    if (bracket === 2) return 'Among the bottom 10% globally.';
    if (bracket === 3) return null;
    if (bracket === 4) return 'Among the top 10% globally.';
    if (bracket === 5) return 'Among the top 5% globally.';
    return null;
  }
  getBracketArrow(bracket: number | undefined): string {
    if (!bracket) return '';
    if (bracket === 1) return 'bi-chevron-double-down';
    if (bracket === 2) return 'bi-chevron-down';
    if (bracket === 4) return 'bi-chevron-up';
    if (bracket === 5) return 'bi-chevron-double-up';
    return '';
  }
  getBarColor(score: number): string {
    if (score >= 90) return 'green';
    if (score >= 50) return 'orange';
    return 'red';
  }
  getOrdinalSuffix(rank: number): string {
    if (rank % 10 === 1 && rank % 100 !== 11) return 'st';
    if (rank % 10 === 2 && rank % 100 !== 12) return 'nd';
    if (rank % 10 === 3 && rank % 100 !== 13) return 'rd';
    return 'th';
  }

  getIndustryLevel(level: number | string | undefined) {
    //sub-industry 4 / industry 3/ sub-sector 2/ sector1
    switch (level) {
      case '1':
      case 1:
        return 'Sector';
      case '2':
      case 2:
        return 'Sub-Sector';
      case '3':
      case 3:
        return 'Industry';
      case '4':
      case 4:
        return 'Sub-Industry';
      default:
        return 'Unknown';
    }
  }

  // Method to download the Excel file
  downloadExcel(): void {
    const filteredList = this.competitors_list.map(item => ({
      Rank: item.rank,
      company: item.entity_name,
      isin: item.isin,
      country: item.country,
      company_type: item.companytype,
      size_10y:item.invention_count_120m ? Math.round(item.invention_count_120m) : item.invention_count_120m,
      absolute_growth: item.invention_count_12m ? Math.round(item.invention_count_12m) : item.invention_count_12m,
      relative_growth: item.relative_growth_12m ? Math.round(item.relative_growth_12m * 10) / 10 : item.relative_growth_12m,
      quality_score: item.quality_average_12m ? Math.round(item.quality_average_12m * 10) / 10 : item.quality_average_12m,
      innovation_score: item.innovation_score ? Math.round(item.innovation_score * 10) / 10 : item.innovation_score,
      inventions_all_time: item.n_inventions ? Math.round(item.n_inventions) : item.n_inventions,
    }));

    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(filteredList);

    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    ws['!cols'] = [
      { wch: 10 }, { wch: 20 }, { wch: 10 }, { wch: 10 }, { wch: 15 },
      { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 20 }
    ];
    const sheetName = "private_competitors";
    XLSX.utils.book_append_sheet(wb, ws, sheetName);

// if the tab name is too big we shorten it to 30 characters
    const tabName = this.tab_name ? this.tab_name.substring(0, 30) : 'private_competitors';
    const fileName = this.tab_name ? `${tabName}_private_competitors.xlsx` : 'excel_file.xlsx';
    XLSX.writeFile(wb, fileName);
  }

  getCategoryDescription(company: IPeerGroupCompetitors): string {
    const companyName = company.entity_name || 'Unknown Company';
    const category = this.categoryTabs.find(cat => cat.key === this.selectedCategory);
    const subindustryName = company.gics_custom_name || company.gsubind_extended_name || company.subindustryname;
    const industryLevel = this.getIndustryLevel(company.gics_custom_level);
    if (!category) return '';

    const isCompanyInCategory = this.filteredCompanyList.some(c => c.entityid === company.entityid);
    const hasCountryFilter = this.countriesControl.value && this.countriesControl.value.length > 0;
    const displayCount = hasCountryFilter ? this.filteredCompanyList.length : this.getBaseCategoryCount();
    const countryFilterText = hasCountryFilter ? ' and selected countries' : '';

    switch (this.selectedCategory) {
      case 'innovation_leaders':
        if (isCompanyInCategory) {
          return `<b>${companyName}</b> ranks <b>${company.rank}${this.getOrdinalSuffix(company.rank)}</b> among the <b>${displayCount}</b> <b>Innovation Leaders</b> in the selected peer group and the GICS ${industryLevel} <b>${subindustryName} (GICS Level ${company.gics_custom_level} out of 4) ${countryFilterText}.`;
        } else {
          return `<b>${companyName}</b> is not one of the <b>Innovation Leaders</b> in the selected peer group andthe GICS ${industryLevel} <b>${subindustryName}</b> (Level ${company.gics_custom_level} out of 4) ${countryFilterText}.`;
        }

      case 'growth_leaders':
        if (isCompanyInCategory) {
          const growthRank = this.getGrowthRank(company);
          return `<b>${companyName}</b> ranks <b>${growthRank}${this.getOrdinalSuffix(growthRank)}</b> among the <b>${displayCount}</b> <b>Growth Leaders</b> in the selected peer group and the GICS ${industryLevel} <b>${subindustryName}</b> (Level ${company.gics_custom_level} out of 4) ${countryFilterText}.`;
        } else {
          return `<b>${companyName}</b> is not one of the <b>Growth Leaders</b> in the selected peer group and the GICS ${industryLevel} <b>${subindustryName}</b> (Level ${company.gics_custom_level} out of 4) ${countryFilterText}.`;
        }

      case 'size_leaders':
        if (isCompanyInCategory) {
          const sizeRank = this.getSizeRank(company);
          return `<b>${companyName}</b> ranks <b>${sizeRank}${this.getOrdinalSuffix(sizeRank)}</b> among the <b>${displayCount}</b> <b>Size Leaders</b> in the selected peer group and the GICS ${industryLevel} <b>${subindustryName}</b> (Level ${company.gics_custom_level} out of 4) ${countryFilterText}.`;
        } else {
          return `<b>${companyName}</b> is not one of the <b>Size Leaders</b> in the selected peer group and the GICS ${industryLevel} <b>${subindustryName}</b> (Level ${company.gics_custom_level} out of 4) ${countryFilterText}`;
        }

      case 'upcoming':
        if (isCompanyInCategory) {
          const upcomingrank = this.getUpcomingRank(company);
          return `<b>${companyName}</b> ranks <b>${upcomingrank}${this.getOrdinalSuffix(upcomingrank)}</b> among the <b>${displayCount}</b> <b>Up and Coming</b> in the selected peer group and the GICS ${industryLevel} <b>${subindustryName}</b> (Level ${company.gics_custom_level} out of 4) ${countryFilterText}.`;
        } else {
          return `<b>${companyName}</b> is not one of the <b>Up and Coming</b> in the selected peer group and the GICS ${industryLevel} <b>${subindustryName}</b> (Level ${company.gics_custom_level} out of 4) ${countryFilterText}.`;
        }

      case 'quality_leaders':
        if (isCompanyInCategory) {
          const qualityRank = this.getQualityRank(company);
          return `<b>${companyName}</b> ranks <b>${qualityRank}${this.getOrdinalSuffix(qualityRank)}</b> among the <b>${displayCount}</b> <b>Quality Leaders</b>  competitors in the selected peer group and the GICS ${industryLevel} <b>${subindustryName}</b> (Level ${company.gics_custom_level} out of 4) ${countryFilterText}.`;
        } else {
          return `<b>${companyName}</b> is not one of the <b>Quality Leaders</b> in the selected peer group and the GICS ${industryLevel} <b>${subindustryName}</b> (Level ${company.gics_custom_level} out of 4) ${countryFilterText}.`;
        }

      case 'all_competitors':
      default:
        if (isCompanyInCategory) {
          return `<b>${companyName}</b> ranks <b>${company.rank}${this.getOrdinalSuffix(company.rank)}</b> in innovation strength among <b>${displayCount}</b> competitors in the selected peer group and the GICS ${industryLevel} <b>${subindustryName}</b> (Level ${company.gics_custom_level} out of 4) ${countryFilterText}.`;
        } else {
          return `<b>${companyName}</b> is not present in the current peer group selection and the GICS ${industryLevel} <b>${subindustryName}</b> (Level ${company.gics_custom_level} out of 4) ${countryFilterText}.`;
        }
    }
  }
  private getBaseCategoryCount(): number {
    const baseFiltered = this.applyCategoryFilter([...this.competitors_list]);
    return baseFiltered.length;
  }
  private getGrowthRank(company: IPeerGroupCompetitors): number {
    const index = this.filteredCompanyList.findIndex(c => c.entityid === company.entityid);
    return index >= 0 ? index + 1 : 0;
  }

  private getSizeRank(company: IPeerGroupCompetitors): number {
    const index = this.filteredCompanyList.findIndex(c => c.entityid === company.entityid);
    return index >= 0 ? index + 1 : 0;
  }


  private getQualityRank(company: IPeerGroupCompetitors): number {
    const index = this.filteredCompanyList.findIndex(c => c.entityid === company.entityid);
    return index >= 0 ? index + 1 : 0;
  }

  getUpcomingRank(company: IPeerGroupCompetitors): number {
    const baseFiltered = this.applyCategoryFilter([...this.competitors_list]);
    const upcomingCompanies = baseFiltered.filter(c => {
      const inv1y = c.invention_count_12m;
      const inv10y = c.invention_count_120m;
      const growth = c.relative_growth_12m;
      return inv1y >= 10 && growth > 0 && (inv10y > 0 ? (inv1y / inv10y) >= 0.33 : false);
    });

    const sortedCompanies = upcomingCompanies.sort((a, b) => (b.innovation_score) - (a.innovation_score));
    const companyIndex = sortedCompanies.findIndex(c => c.entityid === company.entityid);
    return companyIndex >= 0 ? companyIndex + 1 : 0;
  }


}
